'use client';

import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import { FC } from 'react';

export interface UseCaseCustomerCarouselProps {
  title: string;
  masterMarginsTop: boolean;
  masterMarginsBottom: boolean;

  customers: {
    icon: React.ReactNode;
    quote: React.ReactNode;
    authorName: string;
    authorTitle: string;
  }[];
}

const UseCaseCustomerCarousel: FC<UseCaseCustomerCarouselProps> = ({ title, customers = [], masterMarginsTop, masterMarginsBottom }) => {
  const [sliderRef, slider] = useKeenSlider({
    loop: true,
    drag: true,
    slides: {
      origin: 'center',
      perView: 1,
      spacing: 32,
    },
    breakpoints: {
      '(min-width: 600px)': {
        slides: {
          origin: 'center',
          perView: 2,
          spacing: 32,
        },
      },
      '(min-width: 1000px)': {
        slides: {
          origin: 'center',
          perView: 2.75,
          spacing: 32,
        },
      },
      '(min-width: 1600px)': {
        slides: {
          origin: 'center',
          perView: 4,
          spacing: 32,
        },
      },
    },
  });

  const onClickPrev = () => {
    if (!slider.current) return;

    slider.current.prev();
  };

  const onClickNext = () => {
    if (!slider.current) return;

    slider.current.next();
  };

  const header = (
    <div className="grid md:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop md:text-center">
      <div className="md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4 flex flex-col gap-3">
        <h2 className="font-500 tracking-tight text-30/36 md:text-52/62">{title}</h2>
      </div>
    </div>
  );

  const cardCarousel = (
    <div ref={sliderRef} className="keen-slider overflow-visible">
      {customers.map((customer, index) => (
        <div
          key={`customer-carousel-${customer.authorName?.replace(/\s+/g, '-') ?? index}`}
          className="keen-slider__slide rounded-10 overflow-hidden bg-violet-200 text-violet-900 p-3 md:py-4 md:px-6"
        >
          <div className="flex flex-col gap-3">
            {customer.icon}
            <div className="font-sans text-14/20 md:text-20/28 rich-text">{customer.quote}</div>
            <div className="flex gap-3 items-center">
              <div>
                <p className="text-17/24">{customer.authorName}</p>
                <p className="text-10/14 uppercase tracking-wide text-violet-800">{customer.authorTitle}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const navigation = (
    <div className="flex justify-center items-center gap-[15px]">
      <button onClick={onClickPrev} className="block md:absolute md:top-[50%] md:translate-y-[-50%] md:left-12 z-1">
        <Image
          width={48}
          height={48}
          alt="Arrow"
          src="https://front.com/webassets/rebrand/icon-arrow-carousel.svg"
          className="w-6 h-6 block rotate-180"
        />
      </button>
      <button onClick={onClickNext} className="block md:absolute md:top-[50%] md:translate-y-[-50%] md:right-12 z-1">
        <Image width={48} height={48} alt="Arrow" src="https://front.com/webassets/rebrand/icon-arrow-carousel.svg" className="w-6 h-6 block" />
      </button>
    </div>
  );

  return (
    <div className={`${masterMarginsTop ? `mt-[150px] ` : "mt-8 md:mt-22"} ${masterMarginsBottom ? `mb-[150px] ` : "mb-8 md:mb-22"} overflow-hidden flex flex-col gap-y-6 md:gap-y-8`}>
      <div className="px-container-mobile md:px-container-desktop">
        <div className="max-w-grid mx-auto">{header}</div>
      </div>
      <div className="relative flex flex-col gap-3">
        {cardCarousel}
        <div className="hidden md:block absolute top-0 bottom-0 left-0 h-full w-30 bg-gradient-to-r from-white via-white to-transparent pointer-events-none" />
        <div className="hidden md:block absolute top-0 bottom-0 right-0 h-full w-30 bg-gradient-to-l from-white via-white to-transparent pointer-events-none" />
        {navigation}
      </div>
    </div>
  );
};

export default UseCaseCustomerCarousel;
